import React from 'react';
import PropTypes from 'prop-types';

import {Avatar, Box, StickyTooltip} from 'views/components';
import {CalendarMonth} from 'views/icons';

import {userColours} from 'css/UserColours';

const ConnectionBadge = (props) => {
  const secondTapTimeoutRef = React.useRef(null);

  const {
    connection,
    sx,
  } = props;

  const [isAwaitingSecondTap, setIsAwaitingSecondTap] = React.useState(false);

  const getConnectionDisplayName = (connection) => {
    return [connection.firstName, connection.lastName].filter(Boolean).join(' ');
  };

  const getConnectionDisplayInitials = (connection) => {
    return [connection.firstName?.charAt(0), connection.lastName?.charAt(0)].filter(Boolean).join('').toUpperCase();
  };

  const getConnectionType = (connection) => {
    const resourceRnSplit = connection.connectionRn.split(':');
    const secondResourceRn = resourceRnSplit[2];

    if (secondResourceRn) {
      const [resourceType] = secondResourceRn.split('-');

      if (['LIVEINEVENT', 'FIXEDEVENT', 'RECURRENCEEVENT'].includes(resourceType)) {
        return 'EVENT';
      }
    }

    const baseConnectionRn = connection.connectionRn.match(/^RN:(CLIENT|CAREGIVER)-V1-[^:]+/)?.[0];

    if (baseConnectionRn && baseConnectionRn.startsWith('RN:CLIENT-V1-')) {
      return 'CLIENT';
    }

    if (baseConnectionRn && baseConnectionRn.startsWith('RN:CAREGIVER-V1-')) {
      return 'CAREGIVER';
    }

    return null;
  };

  const connectionType = getConnectionType(connection);

  const getConnectionLink = (connectionRn) => {
    if (connectionType === 'EVENT') {
      return `/schedule/event/${connection.connectionRn}`;
    }

    const baseConnectionRn = connectionRn?.match(/^RN:(CLIENT|CAREGIVER)-V1-[^:]+/)?.[0];

    if (baseConnectionRn?.startsWith('RN:CLIENT-V1-')) {
      return `/client/${baseConnectionRn}`;
    }

    if (baseConnectionRn?.startsWith('RN:CAREGIVER-V1-')) {
      return `/caregiver/${baseConnectionRn}`;
    }

    return null;
  };

  const connectionLink = getConnectionLink(connection.connectionRn);

  const clearSecondTapTimeout = () => {
    if (secondTapTimeoutRef.current) {
      window.clearTimeout(secondTapTimeoutRef.current);
      secondTapTimeoutRef.current = null;
    }
  };

  const resetSecondTapState = () => {
    clearSecondTapTimeout();
    setIsAwaitingSecondTap(false);
  };

  React.useEffect(() => {
    return () => {
      clearSecondTapTimeout();
    };
  }, []);

  const handleClick = () => {
    if (!connectionLink) {
      return;
    }

    const isTouchDevice = window.matchMedia?.('(pointer: coarse)')?.matches || navigator.maxTouchPoints > 0;

    if (isTouchDevice && !isAwaitingSecondTap) {
      setIsAwaitingSecondTap(true);
      clearSecondTapTimeout();
      secondTapTimeoutRef.current = window.setTimeout(() => {
        secondTapTimeoutRef.current = null;
        setIsAwaitingSecondTap(false);
      }, 1500);
      return;
    }

    resetSecondTapState();

    const connectionTab = window.open(connectionLink, '_blank');
    connectionTab?.focus();
  };

  if (connectionType === 'EVENT') {
    return (
      <Box
        component='a'
        href={connectionLink}
        target='_blank'
        rel='noopener noreferrer'
        onClick={(clickEvent) => clickEvent.stopPropagation()}
        sx={{
          ...sx,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#E2DEDA',
          color: 'text.secondary',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        <CalendarMonth
          sx={{
            fontSize: '16px',
            transform: 'scale(1.2)',
          }}
        />
      </Box>
    );
  }

  return (
    <StickyTooltip
      title={getConnectionDisplayName(connection)}
    >
      <Avatar
        alt={getConnectionDisplayName(connection)}
        component={connectionLink ? 'button' : 'div'}
        onClick={connectionLink ? handleClick : undefined}
        onBlur={isAwaitingSecondTap ? resetSecondTapState : undefined}
        sx={{
          ...sx,
          overflow: 'hidden',
          backgroundColor: userColours(connection.connectionRn),
          border: 0,
          p: 0,
          cursor: connectionLink ? 'pointer' : 'default',
          boxShadow: isAwaitingSecondTap ? '0 0 0 3px rgba(25, 118, 210, 0.35)' : 'none',
        }}
      >
        {getConnectionDisplayInitials(connection)}
      </Avatar>
    </StickyTooltip>
  );
};

ConnectionBadge.propTypes = {
  connection: PropTypes.shape({
    connectionRn: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  sx: PropTypes.object,
};

export default React.memo(ConnectionBadge);
